import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { adminService } from "../../services/adminService";
import toast from "react-hot-toast";
import ImageUploadField from "../../components/Common/ImageUploadField";
import { IMAGE_SPECS } from "../../utils/imageSpecs";

interface Props {
  show: boolean;
  onHide: () => void;
  onSave: (offerData: any) => void;
  offer?: any;
  mode: "add" | "edit";
}

const initialForm = {
  title: "",
  listing_image: "",
  detail_image: "",
  terms: "",
  end_date: "",
  status: true,
  show_in_home: false,
};

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    [{ font: [] }],
    [{ size: [] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [
      { list: "ordered" },
      { list: "bullet" },
      { indent: "-1" },
      { indent: "+1" },
    ],
    ["link", "image", "video"],
    [{ align: [] }],
    [{ color: [] }, { background: [] }],
    ["clean"],
  ],
};

const quillFormats = [
  "header", "font", "size",
  "bold", "italic", "underline", "strike", "blockquote",
  "list", "bullet", "indent",
  "link", "image", "video",
  "align", "color", "background",
];

const OfferModal: React.FC<Props> = ({ show, onHide, onSave, offer, mode }) => {
  const [formData, setFormData] = useState(initialForm);

  useEffect(() => {
    if (!show) return;

    if (mode === "edit" && offer) {
      setFormData({
        title: offer.title || "",
        listing_image: offer.listing_image || "",
        detail_image: offer.detail_image || "",
        terms: offer.terms || "",
        end_date: offer.end_date ? offer.end_date.split("T")[0] : "",
        status: offer.status ?? true,
        show_in_home: offer.show_in_home ?? false,
      });
    } else {
      setFormData(initialForm);
    }
  }, [show, offer, mode]);

  const handleChange = (e: any) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const uploadImage = async (file: File): Promise<string | null> => {
    const form = new FormData();
    form.append("file", file);
    const res = await adminService.uploadOfferImage(form);
    if (res?.data?.url) return res.data.url;
    toast.error("Upload failed: no URL returned");
    return null;
  };

  const uploadListingImage = async (file: File): Promise<string | null> => {
    return uploadImage(file);
  };

  const uploadDetailImage = async (file: File): Promise<string | null> => {
    return uploadImage(file);
  };

  const handleSubmit = () => {
    onSave(formData);
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {mode === "add" ? "Add Offer" : "Edit Offer"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Title</Form.Label>
            <Form.Control
              name="title"
              value={formData.title}
              onChange={handleChange}
            />
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Listing Image</Form.Label>
                {formData.listing_image && (
                  <div className="mb-2">
                    <img
                      src={formData.listing_image}
                      alt="Listing"
                      style={{
                        width: "100%",
                        maxHeight: "120px",
                        objectFit: "cover",
                        borderRadius: "6px",
                        border: "1px solid #ddd",
                      }}
                    />
                  </div>
                )}
                <ImageUploadField
                  spec={IMAGE_SPECS.offerListing}
                  onUpload={uploadListingImage}
                  onChange={(url) => setFormData({ ...formData, listing_image: url })}
                  showPreview={false}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Detail Image</Form.Label>
                {formData.detail_image && (
                  <div className="mb-2">
                    <img
                      src={formData.detail_image}
                      alt="Detail"
                      style={{
                        width: "100%",
                        maxHeight: "120px",
                        objectFit: "cover",
                        borderRadius: "6px",
                        border: "1px solid #ddd",
                      }}
                    />
                  </div>
                )}
                <ImageUploadField
                  spec={IMAGE_SPECS.offerDetail}
                  onUpload={uploadDetailImage}
                  onChange={(url) => setFormData({ ...formData, detail_image: url })}
                  showPreview={false}
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Terms &amp; Conditions</Form.Label>
            <div style={{ height: "300px", marginBottom: "16px" }}>
            <ReactQuill
              key={show ? `quill-${mode}-${offer?.id ?? "new"}` : "quill-hidden"}
              theme="snow"
              value={formData.terms}
              onChange={(value) => setFormData((prev) => ({ ...prev, terms: value }))}
              modules={quillModules}
              formats={quillFormats}
              placeholder="Enter terms and conditions..."
              style={{ height: "250px"}}
            />
            </div>
            <Form.Text className="text-muted">
              Use the toolbar above for rich text formatting
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Offer End Date</Form.Label>
            <Form.Control
              type="date"
              name="end_date"
              value={formData.end_date}
              onChange={handleChange}
            />
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Check
                type="switch"
                label="Active"
                name="status"
                checked={formData.status}
                onChange={handleChange}
              />
            </Col>
            <Col md={6}>
              <Form.Check
                type="switch"
                label="Show in Homepage"
                name="show_in_home"
                checked={formData.show_in_home}
                onChange={handleChange}
              />
            </Col>
          </Row>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit}>
          Save Offer
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default OfferModal;